import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle,
  Activity, Target, Eye, Lightbulb, BarChart3, RefreshCw, Calendar, DollarSign,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { trackEvent } from '@/lib/trackEvent';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import BitcoinPriceMaChart, { type ChartPoint } from './BitcoinPriceMaChart';

interface SeriesBlock {
  currentPrice: number;
  ma50: number;
  ma200: number;
  previousMa50: number;
  previousMa200: number;
  priceChange7d: number | null;
  priceChange30d: number | null;
  volume24h: number | null;
  avgVolume30d: number | null;
}

interface MarketData {
  usd: SeriesBlock;
  aud: SeriesBlock;
  fx: { audUsd: number | null; audUsd30dChange: number | null };
  mayerMultiple: number;
  consecutiveClosesBelowMa200: number;
  consecutiveClosesAboveMa200: number;
  daysSinceHalving: number;
  drawdownFromAth: number;
  ath: number;
  support: number;
  resistance: number;
  phase: MarketPhase;
  phaseConfidence: number;
  fearGreed: number | null;
  btcDominance: number | null;
  source?: string;
  cached?: boolean;
  cachedAt?: string;
  dataFreshness?: string;
  chartSeriesAud: ChartPoint[];
}

interface FearGreedData { value: number; classification: string; }

type MarketPhase = 'accumulation' | 'early_bull' | 'mid_bull' | 'late_bull' | 'distribution' | 'correction' | 'bear';
type CrashSignal = 'safe' | 'caution' | 'warning' | 'triggered';

interface PhaseConfig {
  name: string; emoji: string; color: string; bgColor: string;
  description: string; observations: string[];
}

const PHASE_CONFIG: Record<MarketPhase, PhaseConfig> = {
  accumulation: {
    name: 'ACCUMULATION PHASE', emoji: '🟢', color: 'text-green-500', bgColor: 'bg-green-500/10 border-green-500/30',
    description: 'Bottom formation - optimal buying zone. Mayer Multiple < 1.0 with extreme fear in the market.',
    observations: ['Mayer Multiple is below 1.0 — price sits under its own 200-day average, a band historically associated with cycle lows.', 'Sentiment readings are in the fear range while price volatility compresses.', 'Historically, phases with these readings have been followed by multi-month base formation rather than immediate trend continuation.'],
  },
  early_bull: {
    name: 'EARLY BULL PHASE', emoji: '🔵', color: 'text-blue-500', bgColor: 'bg-blue-500/10 border-blue-500/30',
    description: 'Uptrend establishing - favorable for swing trading. Mayer Multiple 1.0–1.3 with improving sentiment.',
    observations: ['Mayer Multiple sits between 1.0 and 1.3 — price is modestly extended above its 200-day average.', 'Pullbacks in this band have historically found buyers near the 50-day MA rather than breaking trend.', 'The 50/200-day MA relationship has not yet confirmed a golden cross in this phase.'],
  },
  mid_bull: {
    name: 'MID BULL PHASE', emoji: '🟡', color: 'text-yellow-500', bgColor: 'bg-yellow-500/10 border-yellow-500/30',
    description: 'Bull market in progress - active trading zone. Mayer Multiple 1.3–1.8 with growing greed.',
    observations: ['Mayer Multiple between 1.3 and 1.8 — price is meaningfully extended above the 200-day average.', 'Sentiment indices are reading greed, which has historically coincided with sharper drawdowns inside intact uptrends.', 'Volatility typically expands from here; prior mid-bull stretches saw 20–30% intra-trend corrections.'],
  },
  late_bull: {
    name: 'LATE BULL / WARNING PHASE', emoji: '🟠', color: 'text-orange-500', bgColor: 'bg-orange-500/10 border-orange-500/30',
    description: 'Overextended - prepare exit strategies. Mayer Multiple > 1.8 with extreme greed.',
    observations: ['Mayer Multiple above 1.8 — price is historically stretched versus its 200-day average.', 'Extreme greed readings have preceded local tops in most prior cycles.', 'Late-bull stretches have historically been where drawdown risk rises fastest relative to remaining upside.'],
  },
  distribution: {
    name: 'DISTRIBUTION / TOP RISK', emoji: '🟠', color: 'text-orange-600', bgColor: 'bg-orange-600/10 border-orange-600/30',
    description: 'Cycle-top risk zone. Mayer Multiple > 2.2 or Pi-Cycle proximity. Smart money distributing.',
    observations: ['Mayer Multiple above 2.2 or Pi-Cycle indicators are converging — both are historic cycle-top markers.', 'On-chain distribution patterns typically emerge in this band as long-term holders realise gains.', 'Prior occurrences of these readings clustered within weeks of cycle highs.'],
  },
  correction: {
    name: 'CORRECTION / CRASH PHASE', emoji: '🔴', color: 'text-red-500', bgColor: 'bg-red-500/10 border-red-500/30',
    description: 'CRASH SIGNAL TRIGGERED - 2+ consecutive daily closes below 200-day MA. Exit altcoins, preserve capital.',
    observations: ['Two or more consecutive daily closes have printed below the 200-day MA — the model\'s crash trigger.', 'Historically, sustained closes below the 200-day MA have preceded deeper altcoin drawdowns than BTC drawdowns.', 'Trend structure is broken until price reclaims and holds the 200-day MA.'],
  },
  bear: {
    name: 'BEAR MARKET PHASE', emoji: '🟣', color: 'text-purple-500', bgColor: 'bg-purple-500/10 border-purple-500/30',
    description: 'Bear market - 50-day MA below 200-day MA, sustained weakness. Wait for accumulation phase.',
    observations: ['The 50-day MA is below the 200-day MA — a bearish alignment that has historically persisted for months, not days.', 'Mayer Multiple readings remain suppressed, indicating price below its long-run average.', 'Prior bear phases resolved only after an extended period of low volatility near the lows.'],
  },
};

const BitcoinMarketPhase = ({ defaultCollapsed = false }: { defaultCollapsed?: boolean }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, maRes] = await Promise.all([
        supabase.functions.invoke('fetch-market-overview'),
        supabase.functions.invoke('fetch-btc-moving-averages'),
      ]);
      const overview = overviewRes.data;
      const d = maRes.data;

      if (!d || d.error || d.dataComplete === false) {
        throw new Error(d?.message || 'Bitcoin market data is temporarily unavailable.');
      }
      if (!d.usd || !d.aud || !Number.isFinite(d.usd.currentPrice) || !Number.isFinite(d.aud.currentPrice)) {
        throw new Error('Bitcoin market data is incomplete.');
      }

      setMarketData({
        usd: d.usd,
        aud: d.aud,
        fx: d.fx || { audUsd: null, audUsd30dChange: null },
        mayerMultiple: Number(d.mayerMultiple) || 0,
        consecutiveClosesBelowMa200: Number(d.consecutiveClosesBelowMa200) || 0,
        consecutiveClosesAboveMa200: Number(d.consecutiveClosesAboveMa200) || 0,
        daysSinceHalving: Number(d.daysSinceHalving) || 0,
        drawdownFromAth: Number(d.drawdownFromAth) || 0,
        ath: Number(d.ath) || 0,
        support: Number(d.support) || d.aud.ma200,
        resistance: Number(d.resistance) || d.aud.currentPrice * 1.05,
        phase: (d.phase as MarketPhase) || 'early_bull',
        phaseConfidence: Number(d.phaseConfidence) || 50,
        fearGreed: Number.isFinite(d.fearGreed) ? d.fearGreed : (overview?.fearGreedIndex ?? null),
        btcDominance: Number.isFinite(overview?.btcDominance) ? overview.btcDominance : null,
        source: d.source,
        cached: Boolean(d.cached),
        cachedAt: d.cachedAt,
        dataFreshness: d.dataFreshness,
        chartSeriesAud: Array.isArray(d.chartSeriesAud) ? d.chartSeriesAud : [],
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching market data:', err);
      setError(err instanceof Error ? err.message : 'Bitcoin market data is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getCrashSignal = (): CrashSignal => {
    if (!marketData) return 'safe';
    const streak = marketData.consecutiveClosesBelowMa200;
    const distFromMa = ((marketData.usd.currentPrice - marketData.usd.ma200) / marketData.usd.ma200) * 100;
    if (streak >= 2) return 'triggered';
    if (streak === 1 || distFromMa < 0) return 'warning';
    if (distFromMa < 15) return 'caution';
    return 'safe';
  };

  const formatPrice = (price: number, currency: 'AUD' | 'USD' = 'AUD'): string =>
    new Intl.NumberFormat(currency === 'AUD' ? 'en-AU' : 'en-US', {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(price);

  const formatVolume = (v: number): string => {
    if (v >= 1e9) return `A$${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `A$${(v / 1e6).toFixed(1)}M`;
    return `A$${v.toFixed(0)}`;
  };

  const formatPercent = (v: number | null): string => {
    if (v === null || v === undefined || !Number.isFinite(v)) return 'Unavailable';
    return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
  };

  const getMayerColor = (m: number): string => {
    if (m < 1.0) return 'text-green-500';
    if (m < 1.8) return 'text-blue-500';
    if (m < 2.2) return 'text-orange-500';
    return 'text-red-500';
  };

  const getMayerLabel = (m: number): string => {
    if (m < 1.0) return 'Undervalued';
    if (m < 1.3) return 'Fair value';
    if (m < 1.8) return 'Mid-cycle';
    if (m < 2.2) return 'Overheated';
    return 'Top risk';
  };

  const getCrossStatus = () => {
    if (!marketData) return { label: 'Unavailable', variant: 'secondary' as const };
    const u = marketData.usd;
    const crossedAbove = u.previousMa50 <= u.previousMa200 && u.ma50 > u.ma200;
    const crossedBelow = u.previousMa50 >= u.previousMa200 && u.ma50 < u.ma200;
    const spread = Math.abs(((u.ma50 - u.ma200) / u.ma200) * 100);
    if (crossedAbove) return { label: 'Golden Cross (50d crossed above 200d)', variant: 'default' as const };
    if (crossedBelow) return { label: 'Death Cross (50d crossed below 200d)', variant: 'destructive' as const };
    if (spread < 1) return { label: 'Converging (50d within 1% of 200d)', variant: 'secondary' as const };
    if (u.ma50 > u.ma200) return { label: `50d above 200d by ${spread.toFixed(1)}%`, variant: 'default' as const };
    return { label: `50d below 200d by ${spread.toFixed(1)}%`, variant: 'secondary' as const };
  };

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur">
        <CardHeader><Skeleton className="h-6 w-64" /></CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !marketData) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-primary" /> Bitcoin Market Phase
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{error || 'Unable to load market data'}</p>
          <Button onClick={fetchData} variant="outline" size="sm" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const crashSignal = getCrashSignal();
  // Crash phase overrides classifier-derived phase
  const effectivePhase: MarketPhase = crashSignal === 'triggered' ? 'correction' : marketData.phase;
  const phaseConfig = PHASE_CONFIG[effectivePhase];
  const distanceFrom200MA = ((marketData.usd.currentPrice - marketData.usd.ma200) / marketData.usd.ma200) * 100;
  const crossStatus = getCrossStatus();
  const volumeStatus = marketData.aud.volume24h !== null && marketData.aud.avgVolume30d !== null
    ? marketData.aud.volume24h > marketData.aud.avgVolume30d ? 'Above' : 'Below'
    : 'Unavailable';
  const cycleDay = marketData.daysSinceHalving;
  const showCgtNote = effectivePhase === 'late_bull' || effectivePhase === 'distribution';

  if (collapsed) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-3 min-w-0">
            <Activity className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">Bitcoin Market Phase Report</span>
                <Badge className={`${phaseConfig.bgColor} ${phaseConfig.color} border-0`}>
                  {phaseConfig.emoji} {phaseConfig.name.replace(' PHASE', '')}
                </Badge>
              </div>
              <div className="text-lg font-bold">{formatPrice(marketData.aud.currentPrice, 'AUD')}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => {
              trackEvent('widget_expanded', { widget: 'bitcoin_market_phase' });
              setCollapsed(false);
            }}
          >
            Expand <ChevronDown className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (

    <Card className="border-border/50 bg-card/50 backdrop-blur overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-primary" /> Bitcoin Market Phase Report
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button onClick={fetchData} variant="ghost" size="sm" className="h-8 w-8 p-0">
              <RefreshCw className="h-4 w-4" />
            </Button>
            {defaultCollapsed && (
              <Button onClick={() => setCollapsed(true)} variant="ghost" size="sm" className="gap-1 text-xs">
                <ChevronUp className="h-3.5 w-3.5" /> Collapse
              </Button>
            )}
          </div>
        </div>
        {lastUpdated && (
          <p className="text-xs text-muted-foreground">
            Updated: {lastUpdated.toLocaleTimeString()} • Display in AUD, signal math in USD
            {marketData.source ? ` • Source: ${marketData.source}` : ''}
            {marketData.cached ? ` • Cached${marketData.dataFreshness ? ` (${marketData.dataFreshness})` : ''}` : ''}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Headline price */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="text-muted-foreground text-xs">BTC Price</div>
          <div className="font-bold text-2xl">{formatPrice(marketData.aud.currentPrice, 'AUD')}</div>
          <div className="text-xs text-muted-foreground mt-1">
            USD signal basis: {formatPrice(marketData.usd.currentPrice, 'USD')}
            {marketData.fx.audUsd ? ` • AUD/USD ${marketData.fx.audUsd.toFixed(4)}` : ''}
          </div>
        </div>

        {/* Interactive price + MA chart */}
        <BitcoinPriceMaChart
          data={marketData.chartSeriesAud}
          support={marketData.support}
          resistance={marketData.resistance}
          phaseLabel={phaseConfig.name.replace(' PHASE', '')}
          phaseHsl={
            ({
              accumulation: 'hsl(142 70% 45%)',
              early_bull: 'hsl(210 90% 60%)',
              mid_bull: 'hsl(45 95% 55%)',
              late_bull: 'hsl(25 95% 55%)',
              distribution: 'hsl(15 90% 55%)',
              correction: 'hsl(0 75% 55%)',
              bear: 'hsl(280 70% 60%)',
            } as Record<MarketPhase, string>)[effectivePhase]
          }
        />

        {/* Key metrics grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs">200-Day MA (AUD)</div>
            <div className="font-semibold">{formatPrice(marketData.aud.ma200)}</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs">Distance from 200-Day (USD)</div>
            <div className={`font-semibold ${distanceFrom200MA >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {distanceFrom200MA >= 0 ? '+' : ''}{distanceFrom200MA.toFixed(1)}%
            </div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs">Fear & Greed</div>
            <div className="font-semibold">{marketData.fearGreed ?? 'N/A'}</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs">50-Day MA (AUD)</div>
            <div className="font-semibold">{formatPrice(marketData.aud.ma50)}</div>
          </div>
        </div>

        {/* Cycle & valuation tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs flex items-center gap-1">
              <BarChart3 className="h-3 w-3" /> Mayer Multiple
            </div>
            <div className={`font-bold text-base ${getMayerColor(marketData.mayerMultiple)}`}>
              {marketData.mayerMultiple.toFixed(2)}
            </div>
            <div className="text-xs text-muted-foreground">{getMayerLabel(marketData.mayerMultiple)}</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs flex items-center gap-1">
              <TrendingDown className="h-3 w-3" /> Drawdown from ATH
            </div>
            <div className={`font-semibold ${marketData.drawdownFromAth > -10 ? 'text-green-500' : marketData.drawdownFromAth > -25 ? 'text-yellow-500' : 'text-red-500'}`}>
              {marketData.drawdownFromAth.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">ATH {formatPrice(marketData.ath, 'USD')}</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Days since halving
            </div>
            <div className="font-semibold">{cycleDay}</div>
            <div className="text-xs text-muted-foreground">Post-halving cycle</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-muted-foreground text-xs flex items-center gap-1">
              <DollarSign className="h-3 w-3" /> AUD/USD
            </div>
            <div className="font-semibold">{marketData.fx.audUsd ? marketData.fx.audUsd.toFixed(4) : 'N/A'}</div>
            <div className={`text-xs ${(marketData.fx.audUsd30dChange ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              30d: {formatPercent(marketData.fx.audUsd30dChange)}
            </div>
          </div>
        </div>

        <Separator />

        {/* Market Phase */}
        <div className={`rounded-lg border p-4 ${phaseConfig.bgColor}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{phaseConfig.emoji}</span>
            <h3 className={`font-bold text-lg ${phaseConfig.color}`}>{phaseConfig.name}</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{phaseConfig.description}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Phase confidence</span><span>{marketData.phaseConfidence}%</span>
            </div>
            <Progress value={marketData.phaseConfidence} className="h-1.5" />
          </div>
          {showCgtNote && (
            <p className="text-xs text-orange-500/90 mt-3 italic">
              🇦🇺 AU tax note: Holding crypto &gt;12 months qualifies for the 50% CGT discount — review holdings before realising gains.
            </p>
          )}
        </div>

        {/* Crash Signal */}
        <div className="bg-muted/20 rounded-lg p-4">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-yellow-500" /> Crash Signal Status
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              {crashSignal === 'safe' ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <div className="h-4 w-4 rounded-full border border-muted-foreground/30" />}
              <span className={crashSignal === 'safe' ? 'text-green-500 font-medium' : 'text-muted-foreground'}>SAFE — BTC well above 200-day MA</span>
            </div>
            <div className="flex items-center gap-2">
              {crashSignal === 'caution' ? <AlertTriangle className="h-4 w-4 text-yellow-500" /> : <div className="h-4 w-4 rounded-full border border-muted-foreground/30" />}
              <span className={crashSignal === 'caution' ? 'text-yellow-500 font-medium' : 'text-muted-foreground'}>CAUTION — BTC within 15% of 200-day MA</span>
            </div>
            <div className="flex items-center gap-2">
              {crashSignal === 'warning' ? <AlertTriangle className="h-4 w-4 text-orange-500" /> : <div className="h-4 w-4 rounded-full border border-muted-foreground/30" />}
              <span className={crashSignal === 'warning' ? 'text-orange-500 font-medium' : 'text-muted-foreground'}>WARNING — 1 close below 200-day MA</span>
            </div>
            <div className="flex items-center gap-2">
              {crashSignal === 'triggered' ? <XCircle className="h-4 w-4 text-red-500" /> : <div className="h-4 w-4 rounded-full border border-muted-foreground/30" />}
              <span className={crashSignal === 'triggered' ? 'text-red-500 font-medium' : 'text-muted-foreground'}>
                ⛔ TRIGGERED — {marketData.consecutiveClosesBelowMa200}+ consecutive closes below 200-day MA
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2 italic">
              Streak: {marketData.consecutiveClosesBelowMa200} day(s) below MA200 (USD basis). Triggers at ≥2.
            </p>
          </div>
        </div>

        {/* Technical Picture */}
        <div className="bg-muted/20 rounded-lg p-4">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-primary" /> Technical Picture
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Short-term (50d vs 200d MA):</span>
              <Badge variant={crossStatus.variant} className="ml-2">{crossStatus.label}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Long-term (price vs 200d MA):</span>
              <span className={`ml-2 font-medium ${distanceFrom200MA > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {distanceFrom200MA > 10 ? 'Above trend' : distanceFrom200MA > 0 ? 'Slightly above trend' : 'Below trend'}
                {' '}({distanceFrom200MA >= 0 ? '+' : ''}{distanceFrom200MA.toFixed(1)}%)
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Volume:</span>
              <span className="ml-2">{volumeStatus} 30d average</span>
            </div>
            <div>
              <span className="text-muted-foreground">24h Volume:</span>
              <span className="ml-2">{marketData.aud.volume24h === null ? 'Unavailable' : formatVolume(marketData.aud.volume24h)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Swing low (220d):</span>
              <span className="ml-2 text-green-500">{formatPrice(marketData.support)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Swing high (220d):</span>
              <span className="ml-2 text-red-500">{formatPrice(marketData.resistance)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">7-Day Change:</span>
              <span className={`ml-2 ${(marketData.aud.priceChange7d ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {formatPercent(marketData.aud.priceChange7d)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">30-Day Change:</span>
              <span className={`ml-2 ${(marketData.aud.priceChange30d ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {formatPercent(marketData.aud.priceChange30d)}
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 italic">
            These two readings measure different horizons and can point in opposite directions: the 50d/200d cross tracks
            momentum over recent months, while price vs the 200-day MA tracks the longer-run trend.
          </p>
        </div>

        {/* What the data shows */}
        <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <Lightbulb className="h-4 w-4 text-primary" /> What the Data Shows
          </h4>
          <ul className="space-y-2 text-sm">
            {phaseConfig.observations.map((observation, i) => (
              <li key={i} className="flex items-start gap-2">
                <Target className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>{observation}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-3 italic">
            Observations describe what the indicators currently read. They are not instructions to buy, sell, or size a position.
          </p>
        </div>

        {/* What to Watch */}
        <div className="bg-muted/20 rounded-lg p-4">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <Eye className="h-4 w-4 text-muted-foreground" /> What to Watch
          </h4>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• 200-day MA at {formatPrice(marketData.aud.ma200)} — critical support level</li>
            <li>• Mayer Multiple crossing 1.8 (overheating) or 2.2 (top risk)</li>
            <li>• AUD/USD swings — a {(marketData.fx.audUsd30dChange ?? 0).toFixed(1)}% 30d FX move shifts AUD price independent of BTC</li>
            <li>• Consecutive closes below 200-day MA — currently {marketData.consecutiveClosesBelowMa200} day(s)</li>
          </ul>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          AUD displayed; signal math computed in USD to filter FX noise. General information only —
          not personalised financial advice.{' '}
          <Link to="/disclaimer" className="underline underline-offset-2 hover:text-foreground">
            Read the full disclaimer
          </Link>
          .
        </p>
      </CardContent>
    </Card>
  );
};

export default BitcoinMarketPhase;
